import { FH_COMPONENT, FH_FIELD, FH_FIELD_TYPE, FH_FLAG, FH_PAYLOAD, FH_SECTION_KIND } from './company-form.constants';
import {
  CompanyFieldDescriptor,
  CompanyFormSection,
  FhFieldState,
  FhRebateRow,
  FhUploadedFile,
} from './company-form.types';

// Assembles the request body from the form, keyed by each field's `payload` (which is NOT the
// field id — e.g. billingmail -> invoicing_email). Payload keys are verified against docs/swagger.json in
// makers-finances-mgr (github.com/rappi-inc/makers-finances-mgr, dto/company.CreateRequest).
//
// Hidden sections and hidden fields are skipped entirely: a field the rules engine turned off is
// not part of this company's shape and must not reach the backend.
//
// Two shapes the descriptors cannot express on their own:
//  - FILE fields do not become their own body key. The backend takes a single top-level
//    `documents` array (field_name/document_key/document_name), so every visible FILE field's
//    uploads are collected into that one array instead. This only applies to a company that does
//    not exist yet (see `includeDocuments` below) — per
//    docs/documents/COMPANY_DOCUMENTS_ARCHITECTURE.md §10 ("Key Differences: Create vs Add
//    Documents"), only POST /companies takes documents inline; an existing company's documents are
//    added/removed one at a time via POST/DELETE /companies/:id/documents, which
//    CompanyFilesCardComponent calls directly as soon as the company has an id.
//  - Agency Rebates only ever join THIS body for a brand-new company (`includeAgencyRebate`
//    below) — confirmed against internal/domain/dto/company/create.go in makers-finances-mgr:
//    `CreateRequest.AgencyRebate []AgencyRebateInput` is an inline array POST /companies inserts
//    transactionally along with the company row. PUT /companies/{id} has NO such field (its
//    UpdateRequest is a much smaller, unrelated DTO — internal/app/company/update.go), so an
//    existing company's rebates are synced one row at a time against the dedicated
//    POST/PUT/DELETE /companies/{id}/agency-rebates endpoints instead (see
//    CompanyFormComponent._syncRebates/_loadRebates).

export interface BuildPayloadArgs {
  sections: CompanyFormSection[];
  /** controlName -> runtime state from the rules engine. */
  state: Map<string, FhFieldState>;
  /** controlName -> current value (use form.getRawValue() so disabled controls are included). */
  values: Record<string, unknown>;
  /** Session country; always sent, since the country field is UI-inferred and read-only. */
  country: string;
  /**
   * Whether FILE field uploads should be collected into the top-level `documents` array. Defaults
   * to true (a brand-new company: POST /companies is the only way to attach its first documents).
   * Pass false once the company has an id — its documents are already persisted individually by
   * CompanyFilesCardComponent, and re-sending them on PUT /companies/:id would attempt to re-add
   * documents that already exist (409 Conflict).
   */
  includeDocuments?: boolean;
  /**
   * Whether Agency Rebates rows should be collected into the top-level `agency_rebate` array.
   * Defaults to true (a brand-new company: POST /companies is the only way to attach its first
   * rebates in the same call that creates the company). Pass false once the company has an id —
   * PUT /companies/:id has no `agency_rebate` field at all, so an existing company's rows are
   * synced separately by CompanyFormComponent._syncRebates against the dedicated endpoints.
   */
  includeAgencyRebate?: boolean;
}

export interface DocumentPayload {
  field_name: string;
  document_key: string;
  document_name: string;
}

export function buildCompanyPayload(args: BuildPayloadArgs): Record<string, unknown> {
  const { sections, state, values, country, includeDocuments = true, includeAgencyRebate = true } = args;
  const body: Record<string, unknown> = {};
  const documents: DocumentPayload[] = [];
  const agencyRebate: AgencyRebatePayload[] = [];

  sections.forEach(section => {
    // Checked independently of each field: the caller may hand us per-field state that has not
    // folded section visibility in, and a hidden section must never contribute to the body.
    if (!state.get(sectionStateKey(section.key))?.visible) {
      return;
    }
    if (section.kind === FH_SECTION_KIND.REBATES) {
      if (includeAgencyRebate) {
        agencyRebate.push(...serializeRebates(values[FH_FIELD.AGENCY_REBATES] as FhRebateRow[] | undefined));
      }
      return;
    }
    section.fields.forEach(field => {
      const fieldState = state.get(field.controlName);
      if (!fieldState || !fieldState.visible) {
        return;
      }
      if (field.type === FH_FIELD_TYPE.FILE) {
        if (includeDocuments) {
          documents.push(...serializeDocuments(field, values[field.controlName]));
        }
        return;
      }
      const serialized = serializeField(field, values[field.controlName]);
      if (serialized === undefined) {
        return;
      }
      if (field.valuePayload && field.type === FH_FIELD_TYPE.SELECT && !field.multiple) {
        // Send label to the main payload field and code to the valuePayload field
        const code = serialized as string;
        const option = field.options.find(opt => opt.id === code);
        body[field.payload] = option?.name ?? null;
        body[field.valuePayload] = code;
      } else {
        body[field.payload] = serialized;
      }
    });
  });

  // The country field is inferred from the session and rendered read-only, so it bypasses the
  // visibility gate and is always sent. The DTO also requires it as a non-empty top-level field
  // even though the backend overrides it with the `country` header on arrival.
  body[FH_PAYLOAD.COUNTRY] = country;

  if (documents.length) {
    body['documents'] = documents;
  }

  if (agencyRebate.length) {
    body[FH_PAYLOAD.AGENCY_REBATE] = agencyRebate;
  }

  return body;
}

// The rules engine records section visibility under a synthetic key so payload assembly and the
// template agree on one source of truth.
export function rebatesSectionStateKey(section: CompanyFormSection): string {
  return sectionStateKey(section.key);
}

export function sectionStateKey(sectionKey: string): string {
  return `section:${sectionKey}`;
}

// Returns `undefined` for anything that should be omitted from the body. `false` and `0` are
// meaningful values and are kept. FILE fields never reach here — buildCompanyPayload routes them
// to serializeDocuments instead, since they don't become their own body key.
export function serializeField(field: CompanyFieldDescriptor, raw: unknown): unknown {
  if (isOmitted(raw)) {
    return undefined;
  }

  // Phone numbers are always strings regardless of what the backend declares as the field type.
  if (field.component === FH_COMPONENT.PHONE) {
    return String(raw);
  }

  // `overwrite_payload_type` (companies-form.config.json metadata) lets a field be sent as a
  // different type than the one it renders as — e.g. financial_cut_off_day renders as whatever
  // control the backend's field type calls for, but must reach the API as a number.
  switch (field.overwritePayloadType ?? field.type) {
    case FH_FIELD_TYPE.NUMBER: {
      const parsed = Number(raw);
      return isNaN(parsed) ? undefined : parsed;
    }
    // Also true when a BOOLEAN field rendered as a select hands back the stringified id
    // ("true"/"false") instead of the native boolean it started with — see FH_BOOLEAN_OPTIONS.
    case FH_FIELD_TYPE.BOOLEAN:
      return raw === true || raw === FH_FLAG.TRUE;
    case FH_FIELD_TYPE.SELECT:
      // FhSelectComponent guarantees the control holds an id (or an array of ids).
      return field.multiple
        ? (raw as unknown[]).map(value => String(value))
        : String(raw);
    default:
      return typeof raw === 'string' ? raw.trim() : raw;
  }
}

// Maps a FILE field's uploaded files onto the shape POST/PUT /companies expects inside its single
// top-level `documents` array. `field.payload` (e.g. "tax_document", "contract") becomes
// `field_name`, letting the backend tell which card each document came from.
export function serializeDocuments(field: CompanyFieldDescriptor, raw: unknown): DocumentPayload[] {
  if (isOmitted(raw)) {
    return [];
  }
  return (raw as FhUploadedFile[]).map(file => ({
    field_name: field.payload,
    document_key: file.key,
    document_name: file.name,
  }));
}

function isOmitted(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return true;
  }
  return Array.isArray(value) && value.length === 0;
}

// Body shape for each entry of the inline `agency_rebate` array (FH_PAYLOAD.AGENCY_REBATE) that
// POST /companies accepts. `currency` — not `rebate_currency`, the form's own control name — and
// both amounts travel as fixed 2-decimal strings (e.g. "1000.00" / "5.00"): the backend field is a
// decimal.Decimal, which unmarshals a quoted decimal string exactly as precisely as a bare number.
export interface AgencyRebatePayload {
  currency: string;
  rebate_threshold: string;
  rebate_percentage: string;
}

// Maps the repeatable Agency Rebates rows onto AgencyRebatePayload[]. A row still missing any of
// its three values (e.g. a just-added row on a Save draft, which skips required-field validation)
// is dropped rather than sent half-formed.
export function serializeRebates(rows: FhRebateRow[] | undefined): AgencyRebatePayload[] {
  if (!rows?.length) {
    return [];
  }
  return sortRebates(rows)
    .filter(row => !isOmitted(row.rebate_currency) && !isOmitted(row.rebate_threshold) && !isOmitted(row.rebate_percentage))
    .map(row => ({
      currency: row.rebate_currency,
      rebate_threshold: (row.rebate_threshold as number).toFixed(2),
      rebate_percentage: (row.rebate_percentage as number).toFixed(2),
    }));
}

// Rows are kept sorted in the UI on blur; this re-sorts defensively so the payload is ordered
// regardless of UI state. Rows without a threshold sort last.
export function sortRebates(rows: FhRebateRow[]): FhRebateRow[] {
  return [...rows].sort((a, b) => {
    const left = a?.rebate_threshold;
    const right = b?.rebate_threshold;
    if (left === null || left === undefined) {
      return 1;
    }
    if (right === null || right === undefined) {
      return -1;
    }
    return Number(left) - Number(right);
  });
}
